import { createRoot, RootType } from "react-dom/client";
import React from "react";
import { getStore } from "@utils";
import { appendAsChild } from "@lib/utils/helpers";
import { appendContainer } from "@actions";
import { TranscribeChat } from "../components/Transcribe";
import { Provider } from "../components/Provider";

interface IConveyTranscribeOpts {
  $container: string;
  correlationId: string;
  neuralName: string;
  root?: RootType;
  callerLanguage: string;
  talkback?: boolean;
  showUploader?: boolean;
  conversationId?: string;
  conversationAgencyId?: string;
  $summaryContainer?: string;
  $sentimentContainer?: string;
  $keywordsContainer?: string;
  agentAudio?: File;
  recipientAudio?: File;
  dualChannel?: boolean;
  numberCapabilities?: string;
  interpretationView?: boolean;
  streaming?: boolean;
  callback?: (eventData: Record<any, any>) => void;
  onUIReadyCallback?: () => void;
  onKeywordCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSentimentScoreCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSummaryCallback?: (eventData: string, isNew: boolean) => void;
  onLiveAudioCallback?: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void;
}

export class ConveyTranscribe implements IConveyTranscribeOpts {
  $container: string;
  $element: HTMLElement;
  root?: RootType;
  correlationId: string;
  neuralName: string;
  callerLanguage: string;
  talkback?: boolean;
  showUploader?: boolean;
  conversationId?: string;
  conversationAgencyId?: string;
  $summaryContainer?: string;
  $keywordsContainer?: string;
  $sentimentContainer?: string;
  agentAudio?: File;
  recipientAudio?: File;
  dualChannel?: boolean;
  numberCapabilities?: string;
  interpretationView?: boolean;
  streaming?: boolean;
  mounted: boolean;
  callback?: (eventData: Record<any, any>) => void;
  onKeywordCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSentimentScoreCallback?: (eventData: Record<any, any>, isNew: boolean) => void;
  onSummaryCallback?: (eventData: string, isNew: boolean) => void;
  onLiveAudioCallback?: (eventData: Record<any, any>, dualChannel: boolean, live: boolean) => void;
  onUIReadyCallback?: () => void;

  constructor(params: IConveyTranscribeOpts) {
    const $element = document.getElementById(params.$container);
    this.mounted = false;

    if ($element) {
      const { fullId, $newElement } = appendAsChild(params.correlationId, $element, params.$container, "transcribe");

      getStore().dispatch(
        appendContainer({ phoneNumber: params.correlationId, id: fullId, status: "visible", experience: "transcribe" })
      );

      this.$element = $newElement;
      this.root = params.root;
      this.correlationId = params.correlationId;
      this.callerLanguage = params.callerLanguage;
      this.neuralName = params.neuralName;
      this.$summaryContainer = params.$summaryContainer;
      this.$keywordsContainer = params.$keywordsContainer;
      this.$sentimentContainer = params.$sentimentContainer;
      this.agentAudio = params.agentAudio;
      this.recipientAudio = params.recipientAudio;
      this.dualChannel = params.dualChannel;
      this.numberCapabilities = params.numberCapabilities;
      this.interpretationView = params.interpretationView;
      this.streaming = params.streaming;
      this.conversationId = params.conversationId;
      this.conversationAgencyId = params.conversationAgencyId;
      this.talkback = params.talkback === false ? false : true;
      this.showUploader = params.showUploader === false ? false : true;
      this.callback = params.callback;
      this.onKeywordCallback = params.onKeywordCallback;
      this.onSentimentScoreCallback = params.onSentimentScoreCallback;
      this.onSummaryCallback = params.onSummaryCallback;
      this.onLiveAudioCallback = params.onLiveAudioCallback;
      this.onUIReadyCallback = params.onUIReadyCallback;
      this.init();
    }
  }

  init() {
    if (!this.root) {
      this.root = createRoot(this.$element!);
    }
    this.mounted = true;

    this.root.render(
      <Provider>
        <TranscribeChat
          // @ts-ignore
          correlationId={this.correlationId}
          callerLanguage={this.callerLanguage}
          talkback={this.talkback}
          neuralName={this.neuralName}
          showUploader={this.showUploader}
          $summaryContainer={this.$summaryContainer}
          $sentimentContainer={this.$sentimentContainer}
          $keywordsContainer={this.$keywordsContainer}
          conversationId={this.conversationId}
          conversationAgencyId={this.conversationAgencyId}
          agentAudioFile={this.agentAudio}
          recipientAudioFile={this.recipientAudio}
          dualChannel={this.dualChannel}
          numberCapabilities={this.numberCapabilities}
          interpretationView={this.interpretationView}
          streaming={this.streaming}
          callback={this.callback}
          onKeywordCallback={this.onKeywordCallback}
          onSentimentScoreCallback={this.onSentimentScoreCallback}
          onSummaryCallback={this.onSummaryCallback}
          onLiveAudioCallback={this.onLiveAudioCallback}
          onUIReadyCallback={this.onUIReadyCallback}
        />
      </Provider>
    );
  }

  getRoot() {
    return this.root;
  }

  destroy() {
    if (this.root) {
      this.root.unmount();
      this.mounted = false;
      this.root = null;
      this.$element = null;
      this.correlationId = null;
      this.callerLanguage = null;
      this.neuralName = null;
      this.conversationId = null;
      this.talkback = false;
    }
  }
}
